import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule, MatPrefix } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TNil } from '@app/app.interface';
import { ECurrency } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CurrencyUtilitiesService } from '@utilities/currency-utilities.service';
import { NumberSeparatorUtilitiesService, TNumberSeparator } from '@utilities/number-separator-utilities.service';
import { NgxMaskDirective } from 'ngx-mask';
import { Subscription } from 'rxjs';

@Component({
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatFormFieldModule,
    MatInputModule,
    NgxMaskDirective,
    MatPrefix,
    IconComponent,
  ],
  selector: 'app-budget',
  styleUrls: ['./budget.component.scss'],
  templateUrl: './budget.component.html',
})
export class BudgetComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);

  public readonly control = input.required<FormControl<string | null>>();

  public readonly currency = input<ECurrency | null>(null);
  public readonly notDisclosedLabel = input<string>(this.translate.instant('components.budget.notDisclosed'));

  public readonly currencyIcon = computed(() =>
    this.currency() ? CurrencyUtilitiesService.getCurrencyIcon(this.currency()!) : null,
  );

  public options: string[] = [];

  public decimalSeparator!: TNumberSeparator;
  public thousandSeparator!: TNumberSeparator;

  private readonly numberSeparatorUtilities = inject(NumberSeparatorUtilitiesService);

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('budgetInput');
  private readonly autocompleteRef = viewChild<MatAutocomplete>('auto');

  private lastRawInput = '';
  private controlSubscription: Subscription | null = null;
  private inputSetManually = false;

  constructor() {
    this.decimalSeparator = this.numberSeparatorUtilities.getDecimalSeparator();
    this.thousandSeparator = this.numberSeparatorUtilities.getThousandSeparator();

    effect(() => {
      const control = this.control();

      this.subscribeToControlChanges(control);
    });
  }

  public ngOnDestroy(): void {
    this.controlSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  public onClickOutside(event: Event): void {
    const clickedElement = event.target as HTMLElement;
    const componentElement = clickedElement.closest('app-budget');
    const autocompletePanel = clickedElement.closest('.mat-mdc-autocomplete-panel');
    const matOption = clickedElement.closest('mat-option');

    if (!componentElement && !autocompletePanel && !matOption) {
      this.closeAutocompleteAndSetValue();
    }
  }

  private closeAutocompleteAndSetValue(): void {
    const autocomplete = this.autocompleteRef();

    if (!autocomplete || !autocomplete.isOpen) {
      return;
    }

    const inputElement = this.inputRef()?.nativeElement;

    if (!inputElement) {
      return;
    }

    const currentInputValue: string = inputElement.value;

    inputElement.blur();

    if (!currentInputValue || currentInputValue.trim() === '') {
      this.control().setValue(null);
      this.control().markAsDirty();

      return;
    }

    const normalized: string = this.normalizeBase(currentInputValue);
    this.control().setValue(normalized);
    this.control().markAsDirty();
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue: string = event.option.value as string;

    if (selectedValue === null) {
      this.control().setValue(null);
      this.control().markAsDirty();

      return;
    }

    this.lastRawInput = selectedValue;
    const normalizedFromSelection: string = this.normalizeBase(selectedValue);
    this.control().setValue(normalizedFromSelection);
    this.control().markAsDirty();
    this.options = this.generateNumericOptionsFromRaw(selectedValue);

    this.inputRef()?.nativeElement?.dispatchEvent(new Event('input'));
  }

  public onInputChange(raw: string): void {
    if (this.inputSetManually) {
      this.inputSetManually = false;
      this.lastRawInput = raw;
      this.options = this.generateNumericOptionsFromRaw(raw);

      return;
    }

    this.lastRawInput = raw;

    this.control().markAsDirty();

    const normalized: string = this.normalizeBase(raw);
    this.control().setValue(normalized);

    this.options = this.generateNumericOptionsFromRaw(raw);
  }

  public isOptionSelected(option: string): boolean {
    const controlValue: string | null = this.control().value;

    if (controlValue === null) {
      return false;
    }

    return this.normalizeBase(option) === controlValue;
  }

  private generateNumericOptions(value: string | null): string[] {
    if (this.lastRawInput) {
      return this.generateNumericOptionsFromRaw(this.lastRawInput);
    }

    const base: string = this.normalizeBase(value);

    if (!base) {
      return [];
    }

    let integerDigits: string = base;
    const decimalDigits = '';
    const hasDecimalMarker = false;

    const suggestions: string[] = [];

    suggestions.push(this.formatWithSeparators(integerDigits, decimalDigits, hasDecimalMarker));

    for (let i = 1; i < 5; i++) {
      integerDigits = `${integerDigits}0`;
      suggestions.push(this.formatWithSeparators(integerDigits, decimalDigits, hasDecimalMarker));
    }

    return suggestions;
  }

  private generateNumericOptionsFromRaw(raw: string | null): string[] {
    const parsed = this.stripSeparators(raw);

    let integers = parsed.integerDigits;
    const decimals = parsed.decimalDigits;
    const { hasDecimalMarker } = parsed;

    if (!integers && !decimals) {
      return [];
    }

    if (!integers) {
      integers = '0';
    }

    const suggestions: string[] = [];

    suggestions.push(this.formatWithSeparators(integers, decimals, hasDecimalMarker));

    for (let i = 1; i < 5; i++) {
      integers = `${integers}0`;
      suggestions.push(this.formatWithSeparators(integers, decimals, hasDecimalMarker));
    }

    return suggestions;
  }

  private normalizeBase(value: string | null): string {
    const raw: string = String(value ?? '').replace(/\D/g, '');

    if (!raw) {
      return '';
    }

    const trimmed: string = raw.replace(/^0+/, '');

    if (!trimmed) {
      return '0';
    }

    return trimmed;
  }

  private applyInitialControlValue(control: FormControl<string | null>): void {
    const budgetValue = control.value;
    const el = this.inputRef()?.nativeElement;

    if (!budgetValue || !el) {
      return;
    }

    const parsed = this.stripSeparators(budgetValue);
    const formatted = this.formatWithSeparators(
      parsed.integerDigits,
      parsed.decimalDigits,
      parsed.decimalDigits.length > 0,
    );

    this.setInputValue(formatted, el);
  }

  private subscribeToControlChanges(control: FormControl<string | null>): void {
    this.controlSubscription?.unsubscribe();

    this.lastRawInput = '';

    this.controlSubscription = control.valueChanges.subscribe((value: string | null) => {
      const isInputSetManually = this.inputSetManually;

      if (isInputSetManually) {
        this.inputSetManually = false;

        return;
      }

      if (!value) {
        this.options = [];
        this.setInputValue(null, this.inputRef()?.nativeElement);

        return;
      }

      this.options = this.generateNumericOptions(value);
    });

    this.options = this.generateNumericOptions(control.value);

    if (!control.value) {
      this.setInputValue(null, this.inputRef()?.nativeElement);

      return;
    }

    this.applyInitialControlValue(control);
  }

  private stripSeparators(raw: string | null): {
    integerDigits: string;
    decimalDigits: string;
    hasDecimalMarker: boolean;
  } {
    const value = raw ? String(raw) : '';

    const escapedThousand = this.thousandSeparator.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const withoutThousands = value.replace(new RegExp(escapedThousand, 'g'), '');

    const hasDecimalMarker = withoutThousands.includes(this.decimalSeparator);
    const parts = withoutThousands.split(this.decimalSeparator);

    const integerDigits = (parts[0] || '').replace(/\D/g, '');
    const decimalDigits = (parts[1] || '').replace(/\D/g, '').slice(0, 2);

    return { decimalDigits, hasDecimalMarker, integerDigits };
  }

  private formatWithSeparators(integerDigits: string, decimalDigits: string, hasDecimalMarker: boolean): string {
    const intReversed = integerDigits.split('').reverse().join('');
    const grouped: string[] = [];

    for (let i = 0; i < intReversed.length; i += 3) {
      grouped.push(intReversed.substring(i, i + 3));
    }

    const withThousands: string = grouped
      .map((g) => g.split('').reverse().join(''))
      .reverse()
      .join(this.thousandSeparator);

    if (hasDecimalMarker || decimalDigits.length > 0) {
      return `${withThousands}${this.decimalSeparator}${decimalDigits}`;
    }

    return withThousands;
  }

  private setInputValue(value: string | null, element?: HTMLInputElement | TNil): void {
    const budgetInput: HTMLInputElement | TNil = element ?? this.inputRef()?.nativeElement;

    if (!budgetInput) {
      return;
    }

    this.inputSetManually = true;

    budgetInput.value = value ?? '';
    budgetInput.dispatchEvent(new Event('input'));
  }
}
